import React from 'react'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
} from '@mui/material'
import type { SvgIconProps } from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'
import {
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Assignment as SubmissionsIcon,
  Gavel as ModerationIcon,
  Feedback as FeedbackIcon,
  ManageSearch as AuditLogsIcon,
  Security as RoleManagementIcon,
  ArrowBack as ArrowBackIcon,
  Inventory as BrandsIcon,
  Category as CigarLinesIcon,
  HealthAndSafety as CatalogHealthIcon,
  CardMembership as SubscriptionsIcon,
  Article as ContentIcon,
  Settings as SettingsIcon,
  Hub as IntegrationsIcon,
  Backup as BackupIcon,
} from '@mui/icons-material'
import { isNavItemActive } from './sidebarUtils'
import { APP_SHELL_NAV_DRAWER_ID, DRAWER_WIDTH } from './layoutConstants'
import { headerSafeAreaSpacerSx } from './mainContentStyles'
import { useAdminPermissions } from '@hooks/useAdminPermissions'
import { ADMIN_NAV_PERMISSIONS } from '@utils/adminPermissions'
import { useTemporaryAppDrawer } from './useAppShellDrawer'

type NavItemDef = {
  text: string
  Icon: React.ComponentType<SvgIconProps>
  path: string
}

const adminItems: NavItemDef[] = [
  { text: 'Admin Dashboard', Icon: AdminIcon, path: '/admin/dashboard' },
  { text: 'User Management', Icon: PeopleIcon, path: '/admin/users' },
  { text: 'Brand Catalog', Icon: BrandsIcon, path: '/admin/brands' },
  { text: 'Cigar Lines', Icon: CigarLinesIcon, path: '/admin/cigar-lines' },
  { text: 'Catalog Health', Icon: CatalogHealthIcon, path: '/admin/catalog-health' },
  { text: 'Subscriptions', Icon: SubscriptionsIcon, path: '/admin/subscriptions' },
  { text: 'User Backups', Icon: BackupIcon, path: '/admin/backups' },
  { text: 'Content CMS', Icon: ContentIcon, path: '/admin/content' },
  { text: 'Submissions', Icon: SubmissionsIcon, path: '/admin/submissions' },
  { text: 'Moderation', Icon: ModerationIcon, path: '/admin/moderation' },
  { text: 'User Feedback', Icon: FeedbackIcon, path: '/admin/feedback' },
  { text: 'Audit Logs', Icon: AuditLogsIcon, path: '/admin/audit-logs' },
  { text: 'Role Management', Icon: RoleManagementIcon, path: '/admin/roles' },
  { text: 'System Settings', Icon: SettingsIcon, path: '/admin/settings' },
  { text: 'Integrations', Icon: IntegrationsIcon, path: '/admin/integrations' },
]

const itemButtonSx = {
  color: 'primary.contrastText',
  textDecoration: 'none',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
    '& .MuiListItemText-primary': { fontWeight: 600 },
  },
  '& .MuiListItemIcon-root': { color: 'primary.contrastText', minWidth: 40 },
  '& .MuiListItemText-primary': { fontWeight: 500 },
}

const drawerPaperSx = {
  width: DRAWER_WIDTH,
  boxSizing: 'border-box',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  borderRight: '1px solid',
  borderRightColor: 'primary.dark',
  zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer,
  display: 'flex',
  flexDirection: 'column',
}

type AdminSidebarNavProps = {
  onNavClick?: () => void
}

function AdminSidebarNav({ onNavClick }: AdminSidebarNavProps) {
  const location = useLocation()
  const { can, isReady } = useAdminPermissions()

  const visibleItems = adminItems.filter((item) => {
    const required = ADMIN_NAV_PERMISSIONS[item.path]
    if (!required) return true
    if (!isReady) return false
    return can(required)
  })

  const renderItem = (item: NavItemDef) => {
    const active = isNavItemActive(item.path, location.pathname)
    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          component={NavLink}
          to={item.path}
          selected={active}
          aria-current={active ? 'page' : undefined}
          onClick={onNavClick}
          sx={itemButtonSx}
        >
          <ListItemIcon>
            <item.Icon />
          </ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <>
      <Box sx={headerSafeAreaSpacerSx} />

      <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
          ADMIN
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding aria-label="Admin navigation">
          {visibleItems.map(renderItem)}
        </List>
      </Box>

      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
      <List disablePadding>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/app/dashboard"
            onClick={onNavClick}
            sx={itemButtonSx}
          >
            <ListItemIcon>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="Back to App" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  )
}

type AdminSidebarProps = {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const isTemporaryDrawer = useTemporaryAppDrawer()

  const nav = <AdminSidebarNav onNavClick={isTemporaryDrawer ? onMobileClose : undefined} />

  if (isTemporaryDrawer) {
    return (
      <Drawer
        id={APP_SHELL_NAV_DRAWER_ID}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {nav}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': drawerPaperSx,
      }}
    >
      {nav}
    </Drawer>
  )
}

export default AdminSidebar
